import "./index.css";

import React from 'react';
import DialogDataComponent, {executeComponentCallback} from 'Core/components/DialogDataComponent';
import PropTypes from "prop-types";
import {connect} from 'react-redux';
import {reducerStoreKey as campaignReducerStoreKey} from 'Pages/apps/default/projectPages/campaign/reducer';
import {selectors} from 'Core/store/reducers';
import {saveDynamicCampaignRecipientsAction} from 'Pages/apps/default/projectPages/campaign/actions';
import {getGlobalActions} from 'Core/helpers/redux';
import FormWrapper from 'Core/components/advanced/FormWrapper';
import Label from 'Core/components/display/Label';
import {
	getTabTitle
} from 'Pages/apps/default/projectPages/campaign/pages/ItemPage/popups/MessageDefinitionPopup/helper';
import Icon from 'Core/components/display/Icon';
import ImportCustomersFromSegment from 'Components/advanced/ImportCustomersFromSegment';
import ImportCustomersFromGroup from 'Components/advanced/ImportCustomersFromGroup';
import {getCurrentProjectIdFromSession} from 'Helpers/project';
import {getArray, getBool, getString, isset} from 'Core/helpers/data';
import {CustomerGroupsListItemDataObject} from 'Pages/apps/default/customerGroups/dataObjects';
import {cloneDeep, omit, set} from 'lodash';
import {icon_font_close_symbol, icon_font_save_symbol} from 'Config/app';
import {isMacintosh} from 'Core/helpers/system';
import Button, {BUTTON_DISPLAY_TYPE, BUTTON_STYLE} from 'Core/components/display/Button';
import * as pageConfig from 'Pages/apps/default/projectPages/campaign/pages/ItemPage/config';
import {CAMPAIGN_STATUS} from 'Pages/apps/default/projectPages/campaign/const';

/**
 * Get all actions used by this component
 * @type {Object}
 */
const allActions = getGlobalActions({saveDynamicCampaignRecipientsAction});

/**
 * Get state props from Redux store
 * @param {Object} state - Redux store state
 * @return {Object}
 */
const mapStateToProps = state => ({
	campaign: selectors[campaignReducerStoreKey].getCampaignItem(state),
});

class DynamicCampaignRecipientsDialog extends DialogDataComponent {
	constructor(props) {
		super(props, {
			/** @type {DynamicRecipientsDataObject} */
			data: props.recipientsData,
		}, {
			translationPath: 'DynamicCampaignRecipientsDialog',
			domPrefix: 'dynamic-campaign-recipients-dialog',
			disableLoad: true,
			optimizedUpdate: true,
			optimizedUpdateIgnoreProps: ['onClose', 'onChange', 'onSave', 'onDelete', ...Object.keys(allActions)],
		});
		
		// Refs
		this.segmentRef = null;
		this.groupsRef = null;

		// Render methods
		this.renderReadOnly = this.renderReadOnly.bind(this);
	}

	
	// Data methods -----------------------------------------------------------------------------------------------------
	/** @inheritDoc */
	getDataToReturn() {
		/** @type {DynamicRecipientsDataObject} */
		let data = cloneDeep(this.state.data);
		
		if (!!this.segmentRef) set(data, 'conditions', this.segmentRef.getDataToReturn());
		if (!!this.groupsRef) set(data, 'groups', (
			this.groupsRef.getDataToReturn() ? [this.groupsRef.getDataToReturn()] : []
		));
		
		return data;
	}


	// Action methods ---------------------------------------------------------------------------------------------------
	/**
	 * Validates and saves the data and triggers component's onSave event with both request and response data
	 */
	save() {
		const {dialogGUIID, saveDynamicCampaignRecipientsAction} = this.props;
		const data = this.getDataToReturn();

		// Do the validation
		const isValid = this.validate();

		// If validation is successful
		if (isValid) {
			this.executeAbortableAction(saveDynamicCampaignRecipientsAction, data)
				.then(res => {
					if (!!res) this.close();
					return executeComponentCallback(this.props.onSave, data, res, dialogGUIID);
				});
		}
	}
	

	// Render methods ---------------------------------------------------------------------------------------------------
	/**
	 * Render dialog action buttons
	 * @note Low-level method to render any dialog action buttons.
	 *
	 * @param {DialogButton[]} buttons - Array of buttons to render.
	 * @param {string} [className=''] - Buttons wrapper additional CSS class.
	 * @return {*} Action buttons JSX to use in the main render method.
	 */
	renderActionButtons(buttons = [], className = '') {
		return (
			buttons.length > 0 ?
				<div className={`buttons ${className}`}>
					{buttons.map((button, index) =>
						<Button
							key={index}
							icon={button.icon}
							displayType={button.type ? button.type : BUTTON_DISPLAY_TYPE.SOLID}
							displayStyle={button.style ? button.style : BUTTON_STYLE.DEFAULT}
							onClick={e => 
								// Wait for 100ms to allow the dialog component to update its state
								setTimeout(() => button.onClick(e), 100)
							}
							disabled={getBool(button, 'disabled')}
							{...omit(button, ['type', 'icon', 'label', 'style', 'disabled', 'onClick'])}
						>
							{button.label ? button.label : null}
						</Button>
					)}
				</div>
				:
				null
		);
	}
	
	/** @inheritDoc */
	renderDataActionButtons({
		saveLabel = undefined, saveIcon = undefined, closeLabel = undefined, closeIcon = undefined,
	} = {}, hideClose = false) {
		const {readOnly} = this.props;
		const autofocus = this.getOption('autoFocusActionButtons');

		let buttons = [];

		// Close/cancel button
		if (isMacintosh() && !hideClose) buttons.push({
			style: BUTTON_STYLE.DEFAULT,
			label: (closeLabel ? closeLabel : this.t('Close', 'general')),
			icon: (isset(closeIcon) && closeLabel !== null ? closeIcon : icon_font_close_symbol),
			onClick: this.close,
		});

		if (!readOnly) {
			buttons.push({
				style: BUTTON_STYLE.SUCCESS,
				label: (saveLabel ? saveLabel : this.t('Save', 'general')),
				icon: (isset(saveIcon) && saveIcon !== null ? saveIcon : icon_font_save_symbol),
				onClick: this.save,
				autofocus,
			});
		}

		// Close/cancel button
		if (!isMacintosh() && !hideClose) buttons.push({
			style: BUTTON_STYLE.DEFAULT,
			label: (closeLabel ? closeLabel : this.t('Close', 'general')),
			icon: (isset(closeIcon) && closeLabel !== null ? closeIcon : icon_font_close_symbol),
			onClick: this.close,
		});

		return this.renderActionButtons(buttons);
	}
	
	/**
	 * Renders the read-only view of the component
	 * @return {JSX.Element} The JSX element representing the read-only view.
	 */
	renderReadOnly() {
		const {campaign} = this.props;
		/** @type {DynamicRecipientsDataObject} */
		const data = this.getData();
		const projectId = getCurrentProjectIdFromSession();

		return this.renderDialog(
			this.renderTitle(
				(
					<div className="campaign-item-dialog-title">
						<Label element="small" content={getTabTitle(campaign)} />
						<Icon className="title-icon" symbol="users" />
						<div className="title-content">
							<Label
								element="div"
								content={this.t(
									'pings_recipients_section',
									'AppsSection.DefaultApp.projectPages.CampaignPage.ItemPage'
								)}
							/>
						</div>
					</div>
				)
			),
			(
				<FormWrapper className="dialog-form">
					<ImportCustomersFromSegment
						readOnly={true}
						collapsable={true}
						defaultCollapsed={false}
						projectId={projectId}
						scrollParentSelector="#dynamic-campaign-recipients-dialog"
						showSubmitButton={false}
						defaultData={getArray(data, 'conditions')}
					/>

					<ImportCustomersFromGroup
						readOnly={true}
						collapsable={true}
						defaultCollapsed={false}
						projectId={projectId}
						scrollParentSelector="#dynamic-campaign-recipients-dialog"
						showSubmitButton={false}
						showInputLabel={false}
						defaultData={new CustomerGroupsListItemDataObject(
							getString(getArray(data, 'groups'), '[0]'), '', '', '', null
						)}
					/> 
				</FormWrapper>
			)
		);
	}

	render() {
		const {readOnly, campaign} = this.props;
		/** @type {DynamicRecipientsDataObject} */
		const data = this.getData();
		const projectId = getCurrentProjectIdFromSession();
		const campaignLaunched = (
			getString(this.props, 'campaign.status') === CAMPAIGN_STATUS.ACTIVE ||
			getString(this.props, 'campaign.status') === CAMPAIGN_STATUS.STOPPED
		);

		if (readOnly) return this.renderReadOnly();
		return this.renderDialog(
			this.renderTitle(
				(
					<div className="campaign-item-dialog-title">
						<Label element="small" content={getTabTitle(campaign)} />
						<Icon className="title-icon" symbol="users" />
						<div className="title-content">
							<Label
								element="div"
								content={this.t(
									'pings_recipients_section',
									'AppsSection.DefaultApp.projectPages.CampaignPage.ItemPage'
								)}
							/>
						</div>
					</div>
				)
			),
			(
				<FormWrapper className="dialog-form">
					{campaignLaunched ?
						<>
							<div className="notice success">
								<Label
									content={
										this.t('publishedCampaignNotice', `${pageConfig.translationPath}.MessageDefinitionPopup`)
									}
								/>
							</div>
							<br/>
						</>
						: null
					}
					
					<ImportCustomersFromSegment
						collapsable={true}
						defaultCollapsed={false}
						projectId={projectId}
						scrollParentSelector="#dynamic-campaign-recipients-dialog"
						showSubmitButton={false}
						defaultData={getArray(data, 'conditions')}
						ref={node => { this.segmentRef = node; }}
					/>
					
					<ImportCustomersFromGroup
						collapsable={true}
						defaultCollapsed={false}
						projectId={projectId}
						scrollParentSelector="#dynamic-campaign-recipients-dialog"
						showSubmitButton={false}
						showInputLabel={false}
						defaultData={new CustomerGroupsListItemDataObject(
							getString(getArray(data, 'groups'), '[0]'), '', '', '', null
						)}
						ref={node => { this.groupsRef = node; }}
					/>
				</FormWrapper>
			)
		);
	}
}

/**
 * Define the component's own props that can be passed to it by parent components
 */
DynamicCampaignRecipientsDialog.propTypes = {
	readOnly: PropTypes.bool,
	/** @type {DynamicRecipientsDataObject} */
	recipientsData: PropTypes.object.isRequired,

	onClose: PropTypes.func,
	/** @type {Function<data: DynamicRecipientsDataObject, savedData: DynamicRecipientsDataObject, dialogGUIID: string>} */
	onSave: PropTypes.func,
};

export default connect(mapStateToProps, allActions, null, {forwardRef: true})(DynamicCampaignRecipientsDialog);